import { ROUTES } from 'src/router/const'
import { devMode } from 'src/config'
import { FAQ_ROUTE_PARAMS } from 'src/services/consts/faq.const'
import { FAQ_ARTICLES } from 'src/services/consts/faq-articles.const'
import { FAQ_TOPICS } from 'src/services/consts/faq-topic.const'

/**
 * Breadcrumbs are declared per route:
 * - `meta.breadcrumb` — static label string, or `(params) => string` for dynamic segments
 * - `meta.breadcrumbParent` — `ROUTES.*` of the crumb that precedes this one in the trail
 *
 * Consumed by `src/composables/app/system/useBreadcrumbs.js`. A missing or empty
 * `meta.breadcrumb` still renders in the trail as `"Missing breadcrumb"` so forgotten
 * labels are obvious during development (app shell, dev POC pages, catch-all).
 */
const routes = [
  {
    path: '/',
    redirect: '/' + ROUTES.APP,
  },

  // App routes
  {
    path: '/' + ROUTES.APP,
    name: ROUTES.APP,
    component: () => import('src/pages/AppIndex.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: ROUTES.HOME,
        component: () => import('src/pages/HomePage.vue'),
        meta: { requiresAuth: true, breadcrumb: 'Dashboard' },
      },
      {
        path: '/' + ROUTES.TICKETS,
        name: ROUTES.TICKETS,
        component: () => import('src/pages/TicketIndex.vue'),
        meta: { requiresAuth: true, breadcrumb: 'Product requests' },
      },
      {
        path: '/request/edit/:ticketStep/:ticketId?',
        name: ROUTES.TICKET_EDIT,
        component: () => import('src/pages/TicketEdit.vue'),
        meta: { requiresAuth: true, breadcrumb: 'Request approval' },
      },
      {
        path: '/' + ROUTES.APPROVALS,
        name: ROUTES.APPROVALS,
        component: () => import('src/pages/ApprovalsIndex.vue'),
        meta: { requiresAuth: true, breadcrumb: 'Product approvals' },
      },
      {
        path: '/' + ROUTES.CONFIRMATIONS,
        name: ROUTES.CONFIRMATIONS,
        component: () => import('src/pages/ConfirmationsIndex.vue'),
        meta: { requiresAuth: true, breadcrumb: 'Product confirmations' },
      },
      {
        path: '/' + ROUTES.POLICIES,
        name: ROUTES.POLICIES,
        component: () => import('src/pages/PolicyIndex.vue'),
        meta: { requiresAuth: true, breadcrumb: 'Product policies' },
      },
      {
        path: '/settings',
        name: ROUTES.SETTINGS,
        redirect: { name: ROUTES.SETTINGS_DETAILS },
        component: () => import('src/pages/SettingsPage.vue'),
        meta: { requiresAuth: true, breadcrumb: 'Settings' },
        children: [
          {
            path: 'details',
            name: ROUTES.SETTINGS_DETAILS,
            component: () => import('src/pages/settings/SettingsDetails.vue'),
            meta: {
              requiresAuth: true,
              breadcrumb: 'User details',
              breadcrumbParent: ROUTES.SETTINGS,
            },
          },
          {
            path: 'integrations',
            name: ROUTES.SETTINGS_INTEGRATIONS,
            component: () => import('src/pages/settings/SettingsIntegrations.vue'),
            meta: {
              requiresAuth: true,
              breadcrumb: 'Integrations',
              breadcrumbParent: ROUTES.SETTINGS,
            },
          },
          {
            path: 'notifications',
            name: ROUTES.SETTINGS_NOTIFICATIONS,
            component: () => import('src/pages/settings/SettingsNotifications.vue'),
            meta: {
              requiresAuth: true,
              breadcrumb: 'Notifications',
              breadcrumbParent: ROUTES.SETTINGS,
            },
          },
        ],
      },
      {
        path: '/faq/:topicId',
        name: ROUTES.FAQ_TOPIC,
        component: () => import('src/pages/FaqTopicPage.vue'),
        meta: {
          requiresAuth: true,
          breadcrumb: (params) =>
            FAQ_TOPICS.find((topic) => topic.id === params[FAQ_ROUTE_PARAMS.TOPIC_ID])?.title ??
            'Topic',
          breadcrumbParent: ROUTES.FAQ,
        },
      },
      {
        path: '/faq/:topicId/:articleId',
        name: ROUTES.FAQ_ARTICLE,
        component: () => import('src/pages/FaqArticlePage.vue'),
        meta: {
          requiresAuth: true,
          breadcrumb: (params) =>
            FAQ_ARTICLES[params[FAQ_ROUTE_PARAMS.ARTICLE_ID]]?.title ?? 'Article',
          breadcrumbParent: ROUTES.FAQ_TOPIC,
        },
      },
      {
        path: '/faq',
        name: ROUTES.FAQ,
        component: () => import('src/pages/FaqIndex.vue'),
        meta: { requiresAuth: true, breadcrumb: 'Help Center' },
      },

      // DEV ONLY: POC pages
      ...(devMode
        ? [
            {
              path: '/' + ROUTES.NOTIFICATIONS_POC,
              name: ROUTES.NOTIFICATIONS_POC,
              component: () => import('src/pages/NotificationsPOC.vue'),
              meta: { requiresAuth: true },
            },
            {
              path: '/' + ROUTES.ERROR_POC,
              name: ROUTES.ERROR_POC,
              component: () => import('src/pages/ErrorPOC.vue'),
              meta: { requiresAuth: true },
            },
          ]
        : []),
    ],
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    name: ROUTES.NOT_FOUND,
    component: () => import('pages/ErrorNotFound.vue'),
  },
]

export default routes
